import React, { useEffect, useMemo, useRef, useState } from "react";
import { EntityGraphDepth, EntityGraphEdge, EntityGraphEdgeKind, EntityGraphNode, EntityGraphNodeDecoration, EntityGraphOverlay } from "./EntityGraph.types";
import {
    buildAdjacency,
    declaredOnlyKeys,
    edgePath,
    filterByDepth,
    fitToView,
    fullClosure,
    graphBoundingBox,
    isAssociationEdge,
    isUpEdge,
    layeredLevels,
    layoutGraph,
    levelsFrom,
    NodePosition,
    pathHighlight,
    ViewTransform,
    zoomAtPoint,
} from "./EntityGraph.utils";

// Node labels use noWrap ellipsis; the tooltip is only useful when the text is actually clipped. Card width is
// fixed (zoom scales the SVG, not the layout box), so measuring once per label is enough.
export function useIsTextTruncated(text: string) {
    const ref = useRef<HTMLElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);
    useEffect(() => {
        const element = ref.current;
        setIsTruncated(!!element && element.scrollWidth > element.clientWidth);
    }, [text]);
    return { ref, isTruncated };
}

export interface UseEntityGraphProps {
    nodes: Array<EntityGraphNode>;
    edges: Array<EntityGraphEdge>;
    rootKey: string;
    onRootChange: (key: string) => void;
    overlay?: EntityGraphOverlay;
}

export interface VisibleGraphNode {
    node: EntityGraphNode;
    position: NodePosition;
    isRoot: boolean;
    isDeclaredOnly: boolean;
    dimmed: boolean;
    decoration: EntityGraphNodeDecoration | undefined;
}

export interface GraphEdgeSegment {
    key: string;
    path: string;
    kinds: Array<EntityGraphEdgeKind>;
    dashed: boolean;
    up: boolean;
    faded: boolean;
    highlighted: boolean;
    dimmed: boolean;
    undirected: boolean;
}

export interface InspectorItem {
    node: EntityGraphNode;
    hops: number;
    decoration: EntityGraphNodeDecoration | undefined;
}

const wheelZoomFactor = 1.12;
const buttonZoomFactor = 1.2;

export function useEntityGraph({ nodes, edges, rootKey, onRootChange, overlay }: UseEntityGraphProps) {
    const [depth, setDepth] = useState<EntityGraphDepth>(2);
    const [showDeclaredOnly, setShowDeclaredOnly] = useState(true);
    const [transform, setTransform] = useState<ViewTransform>({ scale: 1, tx: 0, ty: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const [hoveredKey, setHoveredKey] = useState<string | null>(null);
    const [hoveredEdgeKey, setHoveredEdgeKey] = useState<string | null>(null);

    const canvasRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const panStart = useRef({ x: 0, y: 0 });

    // A content signature that changes only when the graph data actually changes — not when the dialog
    // re-derives nodes/edges into fresh arrays every render, nor when react-query refetches identical data on
    // window focus. Keying the layout memo and the auto-fit effect on this stops both from firing on pans,
    // hovers and no-op background refetches (which otherwise snapped the user's zoom/pan back to fit).
    // Label and leafChips are part of the signature (not just the key) because they both render on the card and
    // feed layoutGraph's within-row ordering tiebreak — a rename that keeps the key must still recompute the
    // memoized nodesByKey so the card doesn't show a stale name. (typeOrder/typeLabel are key-derived.)
    const graphSignature =
        nodes.map((node) => `${node.key}=${node.label}#${node.leafChips.join("~")}`).join(",") +
        "|" +
        edges.map((edge) => `${edge.fromKey}>${edge.toKey}:${edge.dashed ? "d" : ""}${edge.kinds.join("+")}`).join(",");

    // Layout is O(passes · nodes) (barycenter + coordinate sweeps + closures); recompute it only when the
    // graph or the view controls change, never on hover/pan/zoom. web-client/CLAUDE.md permits useMemo when
    // strictly necessary for performance — this qualifies.
    const layout = useMemo(() => {
        const nodesByKey = new Map(nodes.map((node) => [node.key, node]));
        const adjacency = buildAdjacency(nodes, edges);
        const declaredOnly = declaredOnlyKeys(rootKey, nodes, edges);
        const hopLevels = levelsFrom(rootKey, adjacency);
        const depthKeys = filterByDepth(hopLevels, depth);
        const visibleKeys = showDeclaredOnly ? depthKeys : new Set([...depthKeys].filter((key) => !declaredOnly.has(key)));
        // System associations are lateral peer links, excluded here for two reasons: (1) row placement must
        // keep the two systems in one stratum (side by side) rather than stacking one below the other the way
        // a sub-processor chain does, and (2) the inspector's Supports/Depends-on closures describe the focus
        // entity's own dependency register, which a peer (and everything reached only through it) is not part
        // of. Depth/reachability above still counts the association as a hop via the full adjacency.
        const nonAssociationAdjacency = buildAdjacency(
            nodes,
            edges.filter((edge) => !isAssociationEdge(edge))
        );
        const levels = layeredLevels(rootKey, visibleKeys, nodesByKey, nonAssociationAdjacency);
        const associatedKeys = new Set<string>();
        edges.filter(isAssociationEdge).forEach((edge) => {
            if (edge.fromKey === rootKey) associatedKeys.add(edge.toKey);
            if (edge.toKey === rootKey) associatedKeys.add(edge.fromKey);
        });
        const positions = layoutGraph(visibleKeys, levels, nodesByKey, edges);
        // Hiding unconfirmed hides relations, not just bystander cards: a dashed edge between two confirmed
        // cards (a disclosure not part of the focused entity's chain) disappears with the toggle too.
        const visibleEdges = edges.filter((edge) => positions.has(edge.fromKey) && positions.has(edge.toKey) && (showDeclaredOnly || !edge.dashed));
        return {
            nodesByKey,
            declaredOnly,
            levels,
            positions,
            visibleEdges,
            associatedKeys,
            blastRadiusClosure: fullClosure(rootKey, nonAssociationAdjacency.in),
            dependenciesClosure: fullClosure(rootKey, nonAssociationAdjacency.out),
        };
        // nodes/edges are intentionally omitted: graphSignature already captures every content change, and
        // their array identity churns every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [graphSignature, rootKey, depth, showDeclaredOnly]);
    const { nodesByKey, declaredOnly, levels, positions, visibleEdges, associatedKeys, blastRadiusClosure, dependenciesClosure } = layout;

    // Hovering any card lights up every path between it and the focus and drops the rest of the graph back, so
    // "how is this thing connected?" is answered by pointing at it. Cheap and hover-dependent, so it stays out
    // of the layout memo; runs over the visible subgraph so it never routes through depth-hidden cards.
    const highlight = hoveredKey && hoveredKey !== rootKey && positions.has(hoveredKey) ? pathHighlight(hoveredKey, rootKey, visibleEdges) : undefined;

    const visibleNodes: Array<VisibleGraphNode> = [...positions.entries()].map(([key, position]) => {
        const node = nodesByKey.get(key)!;
        return {
            node,
            position,
            isRoot: key === rootKey,
            isDeclaredOnly: declaredOnly.has(key),
            dimmed: !!highlight && !highlight.nodeKeys.has(key),
            decoration: overlay?.decorateNode?.(node),
        };
    });

    const edgeSegments: Array<GraphEdgeSegment> = visibleEdges.map((edge) => {
        const key = `${edge.fromKey}->${edge.toKey}`;
        return {
            key,
            path: edgePath(positions.get(edge.fromKey)!, positions.get(edge.toKey)!, positions),
            kinds: edge.kinds,
            dashed: edge.dashed,
            up: isUpEdge(levels.get(edge.fromKey)!, levels.get(edge.toKey)!),
            faded: declaredOnly.has(edge.fromKey) || declaredOnly.has(edge.toKey),
            highlighted: !!highlight?.edgeKeys.has(key),
            dimmed: !!highlight && !highlight.edgeKeys.has(key),
            undirected: isAssociationEdge(edge),
        };
    });

    // The inspector deliberately enumerates ALL known relationships (dashed/declared included), so its
    // Supports / Depends-on counts describe the full register even when the canvas is filtered to the confirmed
    // chain — the numbers must not shift as the "show relations outside the chain" toggle flips.
    const toInspectorItems = (closure: Map<string, number>): Array<InspectorItem> =>
        [...closure.entries()]
            .sort((a, b) => a[1] - b[1])
            .map(([key, hops]) => {
                const node = nodesByKey.get(key)!;
                return { node, hops, decoration: overlay?.decorateNode?.(node) };
            });

    const rootNode = nodesByKey.get(rootKey);
    const blastRadius = toInspectorItems(blastRadiusClosure);
    const dependencies = toInspectorItems(dependenciesClosure);
    // Directly associated systems are peers, not dependencies, so they get their own inspector section rather
    // than folding into blast radius / dependencies. Always one hop by definition, ordered by name.
    const associated: Array<InspectorItem> = [...associatedKeys]
        .map((key) => nodesByKey.get(key)!)
        .sort((first, second) => first.label.localeCompare(second.label))
        .map((node) => ({ node, hops: 1, decoration: overlay?.decorateNode?.(node) }));

    // The auto-fit effect recomputes the layout from its own dependencies instead of reading render-derived
    // values, so it stays correct regardless of render timing.
    const bounding = graphBoundingBox(positions);
    const boundingRef = useRef(bounding);
    boundingRef.current = bounding;

    const fitView = () => {
        const canvas = canvasRef.current;
        if (!canvas || !boundingRef.current) {
            return;
        }
        const rect = canvas.getBoundingClientRect();
        setTransform(fitToView(boundingRef.current, rect.width, rect.height));
    };

    const fitViewRef = useRef(fitView);
    fitViewRef.current = fitView;

    // Fit on re-root / depth / toggle / genuine data changes only — keyed on the content signature, not the
    // nodes/edges array identity, so a window-focus refetch of identical data no longer resets zoom/pan. On
    // re-root the new graph arrives async: rootKey changes first (fits the still-showing previous graph), then
    // the signature changes when the data lands (fits the new graph).
    useEffect(() => {
        fitViewRef.current();
    }, [rootKey, depth, showDeclaredOnly, graphSignature]);

    useEffect(() => {
        const onResize = () => fitViewRef.current();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) {
            return;
        }
        const onWheel = (event: WheelEvent) => {
            event.preventDefault();
            const rect = svg.getBoundingClientRect();
            const factor = event.deltaY < 0 ? wheelZoomFactor : 1 / wheelZoomFactor;
            setTransform((current) => zoomAtPoint(current, event.clientX - rect.left, event.clientY - rect.top, factor));
        };
        svg.addEventListener("wheel", onWheel, { passive: false });
        return () => svg.removeEventListener("wheel", onWheel);
    }, []);

    const zoomCentered = (factor: number) => {
        const svg = svgRef.current;
        if (!svg) {
            return;
        }
        const rect = svg.getBoundingClientRect();
        setTransform((current) => zoomAtPoint(current, rect.width / 2, rect.height / 2, factor));
    };

    const onPointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
        setIsPanning(true);
        panStart.current = { x: event.clientX, y: event.clientY };
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
        if (!isPanning) {
            return;
        }
        const deltaX = event.clientX - panStart.current.x;
        const deltaY = event.clientY - panStart.current.y;
        panStart.current = { x: event.clientX, y: event.clientY };
        setTransform((current) => ({ ...current, tx: current.tx + deltaX, ty: current.ty + deltaY }));
    };

    const onPointerUp = () => setIsPanning(false);

    return {
        depth,
        onDepthChange: setDepth,
        showDeclaredOnly,
        onShowDeclaredOnlyChange: setShowDeclaredOnly,
        hasDeclaredOnly: declaredOnly.size > 0,
        transform,
        zoomPercent: Math.round(transform.scale * 100),
        zoomIn: () => zoomCentered(buttonZoomFactor),
        zoomOut: () => zoomCentered(1 / buttonZoomFactor),
        fitView,
        canvasRef,
        svgRef,
        isPanning,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        rootNode,
        visibleNodes,
        edgeSegments,
        hoveredEdgeSegment: edgeSegments.find((segment) => segment.key === hoveredEdgeKey),
        blastRadius,
        dependencies,
        associated,
        onRootChange,
        onNodeHoverChange: setHoveredKey,
        onEdgeHoverChange: setHoveredEdgeKey,
        overlay,
    };
}
